import React, { useState, useEffect, useMemo, useRef } from "react";
import { 
  Search, Download, Plus, AlertCircle, Loader2, PanelRightClose, Filter, ChevronDown, ChevronLeft, ChevronRight, Check
} from "lucide-react";
import CreateAssignmentModal from "../components/AssignmentModal";
import api from "../../connector/api";

const GradebookTab = ({
  loading,
  error,
  activeClass,
  activeStudents,
  selectedSubjectId,
  isRightSidebarOpen,
  setIsRightSidebarOpen,
  addToast,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedGradeBracket, setSelectedGradeBracket] = useState("all");
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState("all");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isCatDropdownOpen, setIsCatDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const catDropdownRef = useRef(null);

  // State for Grade Brackets, Categories, Assignments & Student Grades
  const [gradeBrackets, setGradeBrackets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [assessments, setAssessments] = useState([]);
  const [assessmentsLoading, setAssessmentsLoading] = useState(false);
  const [studentGrades, setStudentGrades] = useState({});
  const [assignmentOrder, setAssignmentOrder] = useState([]);

  // Navigation & Row Focus State
  const [focusedStudentId, setFocusedStudentId] = useState(null);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const saveTimeoutsRef = useRef({});
  const inputRefs = useRef({});
  
  // Ref to hold absolute latest studentGrades to prevent fast-input race conditions
  const studentGradesRef = useRef(studentGrades);
  useEffect(() => {
    studentGradesRef.current = studentGrades;
  }, [studentGrades]);

  // Click outside listener for custom dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (catDropdownRef.current && !catDropdownRef.current.contains(event.target)) {
        setIsCatDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Format Helper: Formats decimal value safely keeping 1 decimal place if floating
  const formatRawScore = (val) => {
    if (val === "" || val === null || val === undefined) return "";
    const num = Number(val);
    if (isNaN(num)) return "";
    return Number.isInteger(num) ? `${num}.0` : String(num);
  };

  // Helper: Safely resolve category weight percentage dynamically
  const getCategoryWeight = (asm) => {
    const catId = asm.category_id || (typeof asm.category === "object" ? asm.category?.id : asm.category);
    const foundCat = categories.find((c) => String(c.id) === String(catId));
    if (foundCat && foundCat.weight !== undefined) {
      return `${foundCat.weight}%`;
    }
    if (typeof asm.category === "object" && asm.category?.weight !== undefined) {
      return `${asm.category.weight}%`;
    }
    return "--%";
  };

  // Helper: Format Date string safely
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return isNaN(date.getTime())
      ? ""
      : date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  // 1. Fetch Grade Brackets
  useEffect(() => {
    const fetchGradeBrackets = async () => {
      try {
        const response = await api.get("/grade-brackets/");
        const data = Array.isArray(response.data) ? response.data : response.data.results || [];
        const sorted = [...data].sort((a, b) => Number(b.min_score) - Number(a.min_score));
        setGradeBrackets(sorted);
      } catch (err) {
        console.error("Error loading grade brackets:", err);
      }
    };
    fetchGradeBrackets();
  }, []);

  // Fetch Categories
  useEffect(() => {
    const fetchGradeCategories = async () => {
      setCategoriesLoading(true);
      try {
        const response = await api.get("/grade-categories/");
        const data = response.data;
        setCategories(Array.isArray(data) ? data : data.results || []);
      } catch (err) {
        console.error("Error loading grade categories:", err);
        addToast?.("Could not load grade categories", "error");
      } finally {
        setCategoriesLoading(false);
      }
    };
    fetchGradeCategories();
  }, [addToast]);

  // Fetch Assignments & Saved Grades via Single Matrix Endpoint (Solves Pagination Issue)
  useEffect(() => {
    const fetchData = async () => {
      if (!activeClass?.id) return;
      setAssessmentsLoading(true);
      try {
        const matrixResponse = await api.get("/gradebook/assignments/matrix/", {
          params: { 
            classroom: activeClass.id, 
            subject_assignment: selectedSubjectId 
          },
        });

        const { assignments = [], grades = [] } = matrixResponse.data || {};

        const formattedAssignments = assignments.map((asm) => ({
          id: asm.id,
          title: asm.title,
          description: asm.description || "",
          category_id: asm.category?.id || asm.category,
          maxPoints: Number(asm.max_points),
          dueDate: asm.due_date || "",
          created_at: asm.created_at || new Date().toISOString(),
        }));

        setAssessments(formattedAssignments);
        setAssignmentOrder(formattedAssignments.map((a) => String(a.id)));

        const gradeMap = {};
        grades.forEach((item) => {
          const studentId = String(item.student_id ?? item.student);
          const assignmentId = String(item.assignment_id ?? item.assignment);
          const key = `${studentId}_${assignmentId}`;

          gradeMap[key] = {
            id: item.id,
            score: formatRawScore(item.score),
          };
        });

        setStudentGrades(gradeMap);
      } catch (err) {
        console.error("Error loading gradebook matrix:", err);
        addToast?.("Could not load gradebook matrix data", "error");
      } finally {
        setAssessmentsLoading(false);
      }
    };

    fetchData();
  }, [activeClass?.id, selectedSubjectId, addToast]);

  // Category Color Helper
  const getCategoryColor = (asm) => {
    const foundCat = categories.find(
      (c) => String(c.id) === String(asm.category_id || asm.category)
    );
    return foundCat?.color || "#3B82F6";
  };

  // Calculate Overall Score & Letter Grade
  const calculateStudentOverallScore = (studentId) => {
    const categoryStats = {};

    assessments.forEach((asm) => {
      const key = `${String(studentId)}_${String(asm.id)}`;
      const scoreVal = studentGrades[key]?.score;

      if (scoreVal !== "" && scoreVal !== null && scoreVal !== undefined) {
        const catId = String(asm.category_id || asm.category);
        const categoryObj = categories.find((c) => String(c.id) === catId);
        const weight = categoryObj?.weight ?? 100;

        if (!categoryStats[catId]) {
          categoryStats[catId] = {
            earned: 0,
            possible: 0,
            weight: Number(weight),
          };
        }

        categoryStats[catId].earned += Number(scoreVal);
        categoryStats[catId].possible += Number(asm.maxPoints);
      }
    });

    const activeCategoryKeys = Object.keys(categoryStats);
    if (activeCategoryKeys.length === 0) {
      return { formatted: "--", rawPercentage: null, bracketColor: null };
    }

    let totalWeightedScore = 0;
    activeCategoryKeys.forEach((catId) => {
      const { earned, possible, weight } = categoryStats[catId];
      if (possible > 0) {
        const categoryPercentage = earned / possible;
        totalWeightedScore += Math.min(categoryPercentage, 1) * weight;
      }
    });

    const roundedPct = Math.round(totalWeightedScore);

    const matchedBracket = gradeBrackets.find(
      (b) => roundedPct >= Number(b.min_score) && roundedPct <= Number(b.max_score)
    );

    const letterGrade = matchedBracket ? matchedBracket.letter_grade : "";
    const formattedStr = letterGrade ? `${roundedPct}% / ${letterGrade}` : `${roundedPct}%`;

    return { 
      formatted: formattedStr, 
      rawPercentage: roundedPct, 
      bracketId: matchedBracket?.id,
      bracketColor: matchedBracket?.color_code || "#10B981"
    };
  };

  // Custom Manual Column Rearrangement & Category Filtering
    const sortedAssessments = useMemo(() => {
        let baseList = [...assessments];

        // 1. Filter by category if selected
        if (selectedCategoryFilter !== "all") {
            baseList = baseList.filter(
            (a) => String(a.category_id || a.category) === String(selectedCategoryFilter)
            );
        }

        // 2. Sort base list numerically by ID descending (Latest created first)
        baseList.sort((a, b) => Number(b.id) - Number(a.id));

        // 3. Apply custom manual reordering if user shifted columns
        if (assignmentOrder.length === 0) return baseList;
        const asmMap = new Map(baseList.map((a) => [String(a.id), a]));
        const ordered = assignmentOrder.map((id) => asmMap.get(String(id))).filter(Boolean);
        const unmapped = baseList.filter((a) => !assignmentOrder.includes(String(a.id)));
        
        return [...ordered, ...unmapped];
    }, [assessments, assignmentOrder, selectedCategoryFilter]);

  const moveAssignment = (index, direction) => {
    const newOrder = [...sortedAssessments.map((a) => String(a.id))];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= newOrder.length) return;

    const temp = newOrder[index];
    newOrder[index] = newOrder[targetIndex];
    newOrder[targetIndex] = temp;
    setAssignmentOrder(newOrder);
  };

  // Filter Students by Name/ID and Selected Grade Bracket
  const filteredStudents = useMemo(() => {
    if (!activeStudents) return [];
    return activeStudents.filter((student) => {
      const query = searchTerm.toLowerCase().trim();
      const fullName = `${student.first_name || ""} ${student.last_name || ""}`.toLowerCase();
      const studentId = String(student.student_id_number || student.id || "").toLowerCase();
      const matchesSearch = !query || fullName.includes(query) || studentId.includes(query);

      if (!matchesSearch) return false;
      if (selectedGradeBracket === "all") return true;

      const scoreInfo = calculateStudentOverallScore(student.id);
      return String(scoreInfo.bracketId) === String(selectedGradeBracket);
    });
  }, [activeStudents, searchTerm, selectedGradeBracket, studentGrades, assessments, gradeBrackets]);

  // Calculate Column Averages for Bottom Row
  const classAverages = useMemo(() => {
    if (filteredStudents.length === 0) return {};

    const totals = {};
    const counts = {};

    let totalOverallPct = 0;
    let validOverallCount = 0;

    filteredStudents.forEach((student) => {
      const overall = calculateStudentOverallScore(student.id);
      if (overall.rawPercentage !== null) {
        totalOverallPct += overall.rawPercentage;
        validOverallCount++;
      }

      sortedAssessments.forEach((asm) => {
        const key = `${String(student.id)}_${String(asm.id)}`;
        const scoreVal = studentGrades[key]?.score;
        if (scoreVal !== "" && scoreVal !== null && scoreVal !== undefined) {
          if (!totals[asm.id]) {
            totals[asm.id] = 0;
            counts[asm.id] = 0;
          }
          totals[asm.id] += Number(scoreVal);
          counts[asm.id] += 1;
        }
      });
    });

    const averages = {
      overall: validOverallCount > 0 ? `${(totalOverallPct / validOverallCount).toFixed(1)}%` : "--%",
    };

    sortedAssessments.forEach((asm) => {
      if (counts[asm.id] > 0) {
        averages[asm.id] = (totals[asm.id] / counts[asm.id]).toFixed(1);
      } else {
        averages[asm.id] = "--";
      }
    });

    return averages;
  }, [filteredStudents, sortedAssessments, studentGrades]);

  // High-Speed Input Save Handling
  const handleScoreChange = (studentId, studentName, asm, value) => {
    if (value !== "" && Number(value) > asm.maxPoints) {
      addToast?.(`Score cannot exceed max points (${asm.maxPoints})`, "error");
      return;
    }

    const key = `${String(studentId)}_${String(asm.id)}`;

    setStudentGrades((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        score: value,
      },
    }));

    if (saveTimeoutsRef.current[key]) {
      clearTimeout(saveTimeoutsRef.current[key]);
    }

    saveTimeoutsRef.current[key] = setTimeout(async () => {
      try {
        const currentRecord = studentGradesRef.current[key];
        const latestValue = currentRecord?.score ?? value;

        const payload = [
          {
            student: studentId,
            score: latestValue === "" ? null : parseFloat(Number(latestValue).toFixed(1)),
            is_missing: false,
            is_excused: false,
            feedback: "",
          },
        ];

        const response = await api.post(`/gradebook/assignments/${asm.id}/bulk-grade/`, payload);

        if (Array.isArray(response.data) && response.data.length > 0) {
          const savedGrade = response.data[0];
          setStudentGrades((prev) => ({
            ...prev,
            [key]: { ...prev[key], id: savedGrade.id },
          }));
        }

        addToast?.(`Saved grade for ${studentName}`, "success");
      } catch (err) {
        console.error("Auto-save grade error:", err);
        addToast?.(`Failed to save grade for ${studentName}`, "error");
      }
    }, 600);
  };

  // Keyboard Navigation
  const handleKeyDown = (e, rowIndex, colIndex) => {
    const totalRows = filteredStudents.length;
    const totalCols = sortedAssessments.length;

    let targetRow = rowIndex;
    let targetCol = colIndex;

    switch (e.key) {
      case "ArrowUp":
        e.preventDefault();
        targetRow = Math.max(0, rowIndex - 1);
        break;
      case "ArrowDown":
        e.preventDefault();
        targetRow = Math.min(totalRows - 1, rowIndex + 1);
        break;
      case "ArrowLeft":
        if (e.target.selectionStart === 0 || e.target.selectionStart === null) {
          e.preventDefault();
          targetCol = Math.max(0, colIndex - 1);
        } else return;
        break;
      case "ArrowRight":
        if (e.target.selectionEnd === e.target.value.length || e.target.selectionEnd === null) {
          e.preventDefault();
          targetCol = Math.min(totalCols - 1, colIndex + 1);
        } else return;
        break;
      default:
        return;
    }

    const nextStudent = filteredStudents[targetRow];
    const nextAsm = sortedAssessments[targetCol];

    if (nextStudent && nextAsm) {
      const nextKey = `${String(nextStudent.id)}_${String(nextAsm.id)}`;
      inputRefs.current[nextKey]?.focus();
    }
  };

  // Modal Handlers
  const handleOpenCreateModal = () => {
    setSelectedAssignment(null);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (asm) => {
    setSelectedAssignment(asm);
    setIsModalOpen(true);
  };

  const handleSaveAssignment = async (formData) => {
    setIsSubmitting(true);

    try {
      if (selectedAssignment) {
        const payload = {
          title: formData.title,
          description: formData.description,
          category: formData.category,
          max_points: formData.maxPoints,
          due_date: formData.dueDate,
        };

        const response = await api.patch(`/gradebook/assignments/${selectedAssignment.id}/`, payload);
        const updatedData = response.data;

        setAssessments((prev) =>
          prev.map((asm) =>
            asm.id === selectedAssignment.id
              ? {
                  ...asm,
                  title: updatedData.title || formData.title,
                  description: updatedData.description || formData.description,
                  category_id: formData.category,
                  maxPoints: Number(updatedData.max_points || formData.maxPoints),
                  dueDate: updatedData.due_date || formData.dueDate,
                }
              : asm
          )
        );

        addToast?.("Assignment updated successfully!", "success");
      } else {
        const payload = {
          title: formData.title,
          description: formData.description,
          classroom: activeClass?.id,
          subject_assignment: selectedSubjectId,
          category: formData.category,
          max_points: formData.maxPoints,
          due_date: formData.dueDate,
        };

        const response = await api.post("/gradebook/assignments/", payload);
        const createdData = response.data;

        const newAsm = {
          id: createdData.id,
          title: createdData.title,
          description: createdData.description || "",
          category_id: formData.category,
          maxPoints: Number(createdData.max_points),
          dueDate: createdData.due_date || "",
          created_at: createdData.created_at || new Date().toISOString(),
        };

        setAssessments((prev) => [newAsm, ...prev]);
        setAssignmentOrder((prev) => [String(newAsm.id), ...prev]);

        addToast?.("Assignment created successfully!", "success");
      }

      setIsModalOpen(false);
    } catch (err) {
      console.error("Assignment save error:", err);
      addToast?.(selectedAssignment ? "Failed to update assignment" : "Failed to create assignment", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteAssignment = async (asmId) => {
    setIsSubmitting(true);
    try {
      await api.delete(`/gradebook/assignments/${asmId}/`);
      setAssessments((prev) => prev.filter((a) => String(a.id) !== String(asmId)));
      setAssignmentOrder((prev) => prev.filter((id) => String(id) !== String(asmId)));
      addToast?.("Assignment deleted", "success");
      setIsModalOpen(false);
    } catch (err) {
      console.error("Assignment delete error:", err);
      addToast?.("Failed to delete assignment", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading || assessmentsLoading) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-12 text-zinc-400">
        <Loader2 className="w-6 h-6 animate-spin mb-2" />
        <span className="text-xs font-semibold">Loading gradebook records...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
        <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-rose-700 max-w-md">
          <AlertCircle className="w-5 h-5 mx-auto mb-2 text-rose-600" />
          <p className="text-xs font-semibold">{error}</p>
        </div>
      </div>
    );
  }

  const selectedBracketObj = gradeBrackets.find((b) => String(b.id) === String(selectedGradeBracket));
  const selectedCatObj = categories.find((c) => String(c.id) === String(selectedCategoryFilter));

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto w-full">
      <style>{`
        input[type="number"]::-webkit-inner-spin-button,
        input[type="number"]::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input[type="number"] {
          -moz-appearance: textfield;
        }
      `}</style>

      {/* Control Bar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 border-b border-zinc-200/80 pb-5">
        <div className="flex items-center gap-2 flex-1 max-w-xl">
          <div className="relative flex-1">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              type="text"
              placeholder="Search student name or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 bg-white border border-zinc-200 rounded-lg text-xs font-medium placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all shadow-xs"
            />
          </div>

          {/* Category Filter Dropdown */}
          <div className="relative shrink-0" ref={catDropdownRef}>
            <button
              type="button"
              onClick={() => setIsCatDropdownOpen(!isCatDropdownOpen)}
              className="flex items-center gap-2 px-3 py-1.5 bg-white border border-zinc-200 rounded-lg shadow-xs hover:bg-zinc-50 transition-colors cursor-pointer text-xs font-semibold text-zinc-700"
            >
              <Filter className="w-3.5 h-3.5 text-zinc-500" />
              {selectedCatObj ? (
                <div className="flex items-center gap-1.5">
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: selectedCatObj.color || "#3B82F6" }}
                  />
                  <span>{selectedCatObj.name}</span>
                </div>
              ) : (
                <span>All Categories</span>
              )}
              <ChevronDown className="w-3.5 h-3.5 text-zinc-400 ml-1" />
            </button>

            {isCatDropdownOpen && (
              <div className="absolute left-0 mt-1.5 w-52 bg-white border border-zinc-200 rounded-lg shadow-lg z-50 py-1 divide-y divide-zinc-100">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedCategoryFilter("all");
                    setIsCatDropdownOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 text-xs font-medium text-zinc-700 hover:bg-zinc-50 flex items-center justify-between"
                >
                  <span>All Categories</span>
                  {selectedCategoryFilter === "all" && <Check className="w-3.5 h-3.5 text-blue-600" />}
                </button>
                <div className="py-1">
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => {
                        setSelectedCategoryFilter(cat.id);
                        setIsCatDropdownOpen(false);
                      }}
                      className="w-full text-left px-3 py-2 text-xs font-medium text-zinc-700 hover:bg-zinc-50 flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className="w-2.5 h-2.5 rounded-full shrink-0"
                          style={{ backgroundColor: cat.color || "#3B82F6" }}
                        />
                        <span>{cat.name} ({cat.weight}%)</span>
                      </div>
                      {String(selectedCategoryFilter) === String(cat.id) && (
                        <Check className="w-3.5 h-3.5 text-blue-600" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Grade Bracket Dropdown */}
          <div className="relative shrink-0" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center gap-2 px-3 py-1.5 bg-white border border-zinc-200 rounded-lg shadow-xs hover:bg-zinc-50 transition-colors cursor-pointer text-xs font-semibold text-zinc-700"
            >
              <Filter className="w-3.5 h-3.5 text-zinc-500" />
              {selectedBracketObj ? (
                <div className="flex items-center gap-1.5">
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: selectedBracketObj.color_code || "#3B82F6" }}
                  />
                  <span>Grade {selectedBracketObj.letter_grade} ({selectedBracketObj.min_score}-{selectedBracketObj.max_score}%)</span>
                </div>
              ) : (
                <span>All Grade Brackets</span>
              )}
              <ChevronDown className="w-3.5 h-3.5 text-zinc-400 ml-1" />
            </button>

            {isDropdownOpen && (
              <div className="absolute left-0 mt-1.5 w-64 bg-white border border-zinc-200 rounded-lg shadow-lg z-50 py-1 divide-y divide-zinc-100">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedGradeBracket("all");
                    setIsDropdownOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 text-xs font-medium text-zinc-700 hover:bg-zinc-50 flex items-center justify-between"
                >
                  <span>All Grade Brackets</span>
                  {selectedGradeBracket === "all" && <Check className="w-3.5 h-3.5 text-blue-600" />}
                </button>
                <div className="py-1">
                  {gradeBrackets.map((bracket) => (
                    <button
                      key={bracket.id}
                      type="button"
                      onClick={() => {
                        setSelectedGradeBracket(bracket.id);
                        setIsDropdownOpen(false);
                      }}
                      className="w-full text-left px-3 py-2 text-xs font-medium text-zinc-700 hover:bg-zinc-50 flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className="w-2.5 h-2.5 rounded-full shrink-0"
                          style={{ backgroundColor: bracket.color_code || "#3B82F6" }}
                        />
                        <span>Grade {bracket.letter_grade} ({bracket.min_score}-{bracket.max_score}%)</span>
                      </div>
                      {String(selectedGradeBracket) === String(bracket.id) && (
                        <Check className="w-3.5 h-3.5 text-blue-600" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 justify-end">
          <button
            type="button"
            onClick={() => addToast?.("Exporting gradebook to CSV...", "success")}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-zinc-200 rounded-lg text-xs font-semibold text-zinc-700 hover:bg-zinc-50 transition-colors shadow-xs cursor-pointer"
          >
            <Download className="w-3.5 h-3.5 text-zinc-500" />
            Export CSV
          </button>
          
          <button
            type="button"
            onClick={handleOpenCreateModal}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold transition-colors shadow-xs cursor-pointer"
          >
            <Plus className="w-3.5 h-3.5" />
            Add Assessment
          </button>

          {!isRightSidebarOpen && (
            <button
              type="button"
              onClick={() => setIsRightSidebarOpen?.(true)}
              className="h-8 px-3 border border-zinc-200 hover:bg-zinc-100 rounded-lg text-zinc-700 transition-colors flex items-center gap-1.5 text-xs font-semibold shrink-0 cursor-pointer shadow-xs"
              title="Open Sidebar"
            >
              <PanelRightClose className="w-4 h-4 text-zinc-600" />
              <span>Summary</span>
            </button>
          )}
        </div>
      </div>

      {/* Gradebook Matrix Table */}
      <div className="bg-white border border-zinc-200 rounded-lg shadow-xs overflow-hidden">
        <div className="overflow-x-auto custom-scroll">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-zinc-50/80 border-b border-zinc-200 text-[11px] font-bold text-zinc-600 uppercase tracking-wider">
                <th className="py-3 px-4 min-w-[200px] bg-white">Student Name</th>
                <th className="py-3 px-4 text-center w-36 bg-zinc-100/80 border-x border-zinc-200">
                  OVERALL GRADE
                </th>

                {sortedAssessments.map((asm, idx) => {
                  const catColor = getCategoryColor(asm);
                  return (
                    <th
                      key={asm.id}
                      className="py-2.5 px-2 text-center min-w-[130px] border-r border-zinc-200/60 last:border-r-0 transition-colors group relative"
                      style={{ backgroundColor: `${catColor}18` }}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <button
                          type="button"
                          disabled={idx === 0}
                          onClick={() => moveAssignment(idx, -1)}
                          className="p-0.5 hover:bg-black/10 rounded disabled:opacity-20 cursor-pointer"
                          title="Move Left"
                        >
                          <ChevronLeft className="w-3 h-3 text-zinc-600" />
                        </button>
                        
                        <div
                          onClick={() => handleOpenEditModal(asm)}
                          className="flex items-center gap-1 cursor-pointer truncate max-w-[80px]"
                          title="Click to edit assignment"
                        >
                          <span
                            className="w-2 h-2 rounded-full shrink-0"
                            style={{ backgroundColor: catColor }}
                          />
                          <span className="font-bold text-zinc-800 truncate">
                            {asm.title}
                          </span>
                        </div>

                        <button
                          type="button"
                          disabled={idx === sortedAssessments.length - 1}
                          onClick={() => moveAssignment(idx, 1)}
                          className="p-0.5 hover:bg-black/10 rounded disabled:opacity-20 cursor-pointer"
                          title="Move Right"
                        >
                          <ChevronRight className="w-3 h-3 text-zinc-600" />
                        </button>
                      </div>

                      {asm.dueDate ? (
                        <div className="text-[9px] text-zinc-400 font-normal">
                          Due: {formatDate(asm.dueDate)}
                        </div>
                      ) : (
                        <div className="text-[9px] text-zinc-400 font-normal">
                          No Due Date
                        </div>
                      )}

                      <div className="text-[10px] text-zinc-500 font-normal">
                        Max: {asm.maxPoints} pts ({getCategoryWeight(asm)})
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 text-xs font-medium text-zinc-700">
              {filteredStudents.length > 0 ? (
                filteredStudents.map((student, rowIndex) => {
                  const isFocused = focusedStudentId === student.id;
                  const overallScore = calculateStudentOverallScore(student.id);

                  return (
                    <tr
                      key={student.id}
                      className={`transition-all duration-150 ${
                        isFocused
                          ? "bg-blue-50/40 ring-2 ring-blue-500/80 relative z-10 shadow-xs"
                          : "hover:bg-zinc-50/60"
                      }`}
                    >
                      <td className="py-3 px-4 font-semibold text-zinc-900">
                        <span>
                          {rowIndex + 1}) {student.last_name}, {student.first_name}
                        </span>
                      </td>

                      <td 
                        className="py-3 px-4 text-center font-bold border-x border-zinc-200"
                        style={{
                          color: overallScore.bracketColor || "#059669",
                          backgroundColor: overallScore.bracketColor ? `${overallScore.bracketColor}12` : "inherit"
                        }}
                      >
                        {overallScore.formatted}
                      </td>

                      {sortedAssessments.map((asm, colIndex) => {
                        const catColor = getCategoryColor(asm);
                        
                        const key = `${String(student.id)}_${String(asm.id)}`;
                        const rawScore = studentGrades[key]?.score ?? "";

                        const itemPercentage = rawScore !== "" && rawScore !== null
                          ? `${Math.round((Number(rawScore) / asm.maxPoints) * 100)}%`
                          : null;

                        return (
                          <td
                            key={asm.id}
                            className="p-0 text-center border-r border-zinc-200/50 last:border-r-0 align-middle"
                            style={{ backgroundColor: `${catColor}08` }}
                          >
                            <div className="flex flex-col items-center justify-center w-full h-full py-1">
                              <input
                                ref={(el) => (inputRefs.current[key] = el)}
                                type="number"
                                step="0.1"
                                min="0"
                                max={asm.maxPoints}
                                value={rawScore}
                                onFocus={() => setFocusedStudentId(student.id)}
                                onBlur={() => setFocusedStudentId(null)}
                                onKeyDown={(e) => handleKeyDown(e, rowIndex, colIndex)}
                                onChange={(e) =>
                                  handleScoreChange(
                                    student.id,
                                    `${student.first_name} ${student.last_name}`,
                                    asm,
                                    e.target.value
                                  )
                                }
                                className="w-full text-center bg-transparent border-0 focus:outline-none focus:bg-transparent font-semibold text-zinc-800 text-xs py-1"
                              />
                              {itemPercentage && (
                                <span className="text-[10px] text-zinc-400 font-medium -mt-1 pointer-events-none">
                                  {itemPercentage}
                                </span>
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={2 + sortedAssessments.length}
                    className="py-12 text-center text-xs text-zinc-400 italic"
                  >
                    No matching student records found.
                  </td>
                </tr>
              )}
            </tbody>

            {/* Class Average Footer Row */}
            {filteredStudents.length > 0 && (
              <tfoot>
                <tr className="bg-zinc-100/90 border-t-2 border-zinc-300 font-bold text-xs text-zinc-800">
                  <td className="py-3 px-4 uppercase tracking-wider bg-zinc-200/60">
                    Class Average
                  </td>
                  <td className="py-3 px-4 text-center border-x border-zinc-200 text-blue-700 bg-zinc-200/40">
                    {classAverages.overall}
                  </td>
                  {sortedAssessments.map((asm) => (
                    <td
                      key={asm.id}
                      className="py-3 px-2 text-center border-r border-zinc-200/80 last:border-r-0 text-zinc-700"
                    >
                      {classAverages[asm.id] !== "--" ? `${classAverages[asm.id]} pts` : "--"}
                    </td>
                  ))}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Assignment Modal */}
      <CreateAssignmentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSaveAssignment}
        onDelete={handleDeleteAssignment}
        initialData={selectedAssignment}
        categories={categories}
        loading={isSubmitting || categoriesLoading}
      />
    </div>
  );
};

export default GradebookTab;